import React from 'react';
import { EnumUserRoute } from '@components-member/userRoutes/enums';
import { userRoute } from '@components-member/userRoutes/userRoute';
import CONSTANTS from '@components-shared/constants';
import ENUM_CONSTANTS from '@components-shared/enum.constants';
import { IEnumConstant } from '@components-shared/enum.constants.interface';
import { FormattedDateTime } from '@components-shared/formattedDateTime';
import { IColumn } from '@components-shared/shared.interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { getFormattedNumber, getLink, getTableLabel, getValueFromEnums } from '@utils/helper';
import { IContextProps, ISalesUserPspOperation } from './interfaces';

export const SALES_USER_PSP_OPERATION_GRID_COLUMNS = [
  {
    key: 'id',
    name: keyMap.sharedFieldsId,
    render: (row: ISalesUserPspOperation, value: string) =>
      getLink(
        value,
        row.type === CONSTANTS.PSP_OPERATIONS.DEPOSIT
          ? userRoute(EnumUserRoute.salesUserDepositDetails).getPath({ id: row.id })
          : userRoute(EnumUserRoute.salesUserWithdrawalDetails).getPath({ id: row.id }),
      ),
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (row: ISalesUserPspOperation, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'user.fullName',
    name: keyMap.sharedFieldsUser,
    render: (row: ISalesUserPspOperation, value: string) =>
      getLink(value, userRoute(EnumUserRoute.salesUserDetails).getPath({ id: row.user.id })),
    isHidden: (ctx: IContextProps) => ctx.props.userId,
  },
  {
    key: 'user.email',
    name: keyMap.sharedFieldsEmail,
    isHidden: (ctx: IContextProps) => ctx.props.userId,
  },
  {
    key: 'accountNumber',
    name: keyMap.pspOperationsTableAccountTitle,
    render: (row: ISalesUserPspOperation, value: string) =>
      row.tradingAccount.id
        ? getLink(value, userRoute(EnumUserRoute.salesUserTradingAccountDetails).getPath({ id: row.tradingAccount.id }))
        : value,
    isHidden: (ctx: IContextProps) => ctx.props.tradingAccountId,
  },
  {
    key: 'amount',
    name: keyMap.sharedFieldsAmount,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'currency',
    name: keyMap.sharedFieldsCurrency,
    render: (row: ISalesUserPspOperation, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.PSP_CURRENCY),
  },
  {
    key: 'amountUSD',
    name: keyMap.pspOperationsTableAmountUSDTitle,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'status',
    name: keyMap.commonStatus,
    render: (row: ISalesUserPspOperation, value: string) => getTableLabel(value, ENUM_CONSTANTS.PSP_OPERATION_STATUS),
  },
  {
    key: 'comment',
    name: keyMap.sharedFieldsComment,
  },
];

export const SALES_USER_PSP_OPERATION_GRID_FILTERS_FIELDS = [
  {
    name: 'createdOn',
    title: keyMap.sharedFieldsCreatedOn,
    type: 'dateTimeRange',
  },
  {
    name: 'user',
    title: keyMap.sharedFieldsUser,
    type: 'input',
    isHidden: (ctx: IContextProps) => !!ctx.props.userId || !!ctx.props.tradingAccountId,
  },
  {
    name: 'status',
    title: keyMap.commonStatus,
    type: 'select',
    options: ENUM_CONSTANTS.PSP_OPERATION_STATUS.filter(
      (x: IEnumConstant) => x.value !== CONSTANTS.PSP_OPERATION_STATUS.ONHOLD,
    ),
  },
  {
    name: 'type',
    type: 'hidden',
    defaultValue: (ctx: IContextProps) => ctx.props.type,
  },
  {
    name: 'userId',
    type: 'hidden',
    defaultValue: (ctx: IContextProps) => ctx.props.userId,
  },
  {
    name: 'tradingAccountId',
    type: 'hidden',
    defaultValue: (ctx: IContextProps) => ctx.props.tradingAccountId,
  },
];

export const SALES_USER_PSP_OPERATION_GRID_ACTIONS = [
  {
    name: keyMap.sharedActionsDetails,
    type: 'link',
    link: (row: ISalesUserPspOperation) =>
      row.type === CONSTANTS.PSP_OPERATIONS.DEPOSIT
        ? EnumUserRoute.salesUserDeposits
        : EnumUserRoute.salesUserWithdrawalDetails,
  },
];

export const SALES_USER_PSP_OPERATION_DETAILS_COLUMNS: IColumn[] = [
  {
    key: 'id',
    name: keyMap.sharedFieldsId,
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (row: ISalesUserPspOperation, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
  {
    key: 'user.fullName',
    name: keyMap.sharedFieldsUser,
    render: (row: ISalesUserPspOperation, value: string) =>
      getLink(value, userRoute(EnumUserRoute.salesUserDetails).getPath({ id: row.user.id })),
  },
  {
    key: 'user.email',
    name: keyMap.sharedFieldsEmail,
  },
  {
    key: 'type',
    name: keyMap.sharedFieldsType,
    render: (row: ISalesUserPspOperation, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.WALLET_OPERATION_TYPE),
  },
  {
    key: 'category',
    name: keyMap.sharedFieldsCategory,
    render: (row: ISalesUserPspOperation, value: string) =>
      getValueFromEnums(value, ENUM_CONSTANTS.WALLET_OPERATION_CATEGORY),
  },
  {
    key: 'status',
    name: keyMap.commonStatus,
    render: (row: ISalesUserPspOperation, value: string) => getTableLabel(value, ENUM_CONSTANTS.PSP_OPERATION_STATUS),
  },
  {
    key: 'accountNumber',
    name: keyMap.pspOperationsTableAccountTitle,
    render: (row: ISalesUserPspOperation, value: string) =>
      row.tradingAccount.id
        ? getLink(value, userRoute(EnumUserRoute.salesUserTradingAccountDetails).getPath({ id: row.tradingAccount.id }))
        : value,
  },
  {
    key: 'amount',
    name: keyMap.sharedFieldsAmount,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'currency',
    name: keyMap.sharedFieldsCurrency,
    render: (row: ISalesUserPspOperation, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.PSP_CURRENCY),
  },
  {
    key: 'amountUSD',
    name: keyMap.pspOperationsTableAmountUSDTitle,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'feeUSD',
    name: keyMap.pspOperationsTableFeeUSDTitle,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'netAmountUSD',
    name: keyMap.pspOperationsTableNetAmountUSDTitle,
    render: (row: ISalesUserPspOperation, value: number) => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'comment',
    name: keyMap.sharedFieldsComment,
  },
];
